import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Crown, Check, Zap, MessageCircle, BarChart3, BookOpen, Sparkles, Loader2, Shield
} from "lucide-react";
import { API_BASE_URL } from "../lib/config";

const API_URL = API_BASE_URL;

const Membership = () => {
  const { user, token, fetchUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/membership/plans`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/membership/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPlans(plansRes.data);
      setMembershipStatus(statusRes.data);
    } catch (error) {
      console.error("Failed to fetch membership data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/membership/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === "paid") {
          toast.success("Payment successful! Welcome to Premium! 🎉");
          await fetchUser();
          await fetchData();
          setCheckingPayment(false);
          // Clear URL params
          window.history.replaceState({}, "", "/membership");
          return;
        }

        if (response.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setCheckingPayment(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.error("Payment verification timed out. Please refresh the page.");
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await axios.post(
        `${API_URL}/membership/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      setProcessingPlan(null);
    }
  };

  const freeFeatures = [
    { text: "5 AI tutor questions per day", included: true },
    { text: "Basic practice questions", included: true },
    { text: "Progress tracking", included: true },
    { text: "Leaderboard access", included: true },
    { text: "Unlimited AI questions", included: false },
    { text: "Advanced analytics", included: false },
    { text: "Exclusive content", included: false },
    { text: "Ad-free experience", included: false },
  ];

  const premiumFeatures = [
    { text: "Unlimited AI tutor questions", included: true },
    { text: "All practice questions", included: true },
    { text: "Advanced progress analytics", included: true },
    { text: "Leaderboard access", included: true },
    { text: "Exclusive premium content", included: true },
    { text: "Priority AI responses", included: true },
    { text: "Ad-free experience", included: true },
    { text: "Early access to new features", included: true },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-bounce-subtle">
          <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>
    );
  }

  const isPremium = membershipStatus?.is_premium;
  const isTrial = membershipStatus?.plan === "trial";
  const trialDaysLeft = isTrial && membershipStatus?.trial_expires_at
    ? Math.max(0, Math.ceil((new Date(membershipStatus.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="space-y-8 animate-slide-up" data-testid="membership-page">
      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 text-center max-w-sm mx-4">
            <Loader2 className="w-12 h-12 text-lime-500 animate-spin mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-slate-800">Processing Payment...</h3>
            <p className="text-slate-500 mt-2">Please wait while we confirm your subscription.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Crown className="w-10 h-10 text-white" />
        </div>
        <h1 className="font-heading font-black text-3xl text-slate-800">
          {isPremium ? "You're Premium! 🎉" : "Upgrade to Premium"}
        </h1>
        <p className="text-slate-500 mt-2 max-w-md mx-auto">
          {isPremium 
            ? "Enjoy unlimited access to all premium features"
            : "Unlock unlimited AI tutoring and exclusive content to ace your exams"
          }
        </p>
      </div>

      {/* Trial Banner */}
      {isTrial && (
        <div className="card-pop bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-black text-xl">7</span>
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-slate-800">
                  Free Trial Active -- {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left
                </p>
                <p className="text-slate-500 text-sm">
                  You have full premium access until {new Date(membershipStatus.trial_expires_at).toLocaleDateString()}. Subscribe to keep it going.
                </p>
              </div>
            </div>
            <div className="px-4 py-2 bg-blue-500 text-white font-bold rounded-full text-sm">
              Trial
            </div>
          </div>
        </div>
      )}

      {/* Current Status Card (if paid premium) */}
      {isPremium && !isTrial && (
        <div className="card-pop bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                <Crown className="w-7 h-7 text-yellow-900" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-slate-800">
                  {membershipStatus.plan === "yearly" ? "Premium Yearly" : "Premium Monthly"}
                </p>
                <p className="text-slate-500 text-sm">
                  Renews on {new Date(membershipStatus.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="px-4 py-2 bg-lime-500 text-white font-bold rounded-full text-sm">
              Active
            </div>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Free Plan */}
        <div className="card-pop">
          <div className="text-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">Free</h3>
            <div className="mt-4">
              <span className="font-heading font-black text-4xl text-slate-800">$0</span>
              <span className="text-slate-500">/forever</span>
            </div>
          </div>
          
          <ul className="space-y-3 mb-6">
            {freeFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                {feature.included ? (
                  <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-slate-200 flex-shrink-0" />
                )}
                <span className={feature.included ? "text-slate-700" : "text-slate-400"}>
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>

          <button
            disabled
            className="w-full py-3 bg-slate-100 text-slate-500 font-bold rounded-full"
          >
            Current Plan
          </button>
        </div>

        {/* Monthly Plan */}
        <div className="card-pop border-indigo-200 relative">
          <div className="text-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">Premium Monthly</h3>
            <div className="mt-4">
              <span className="font-heading font-black text-4xl text-indigo-600">$9.99</span>
              <span className="text-slate-500">/month</span>
            </div>
          </div>

          <ul className="space-y-3 mb-6">
            {premiumFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                <span className="text-slate-700">{feature.text}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => handleSubscribe("monthly")}
            disabled={isPremium || processingPlan === "monthly"}
            data-testid="subscribe-monthly-btn"
            className="w-full btn-indigo disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processingPlan === "monthly" ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : isPremium ? (
              "Already Premium"
            ) : (
              "Subscribe Monthly"
            )}
          </button>
        </div>

        {/* Yearly Plan */}
        <div className="card-pop border-lime-300 relative overflow-hidden">
          {/* Best Value Badge */}
          <div className="absolute top-0 right-0 bg-lime-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
            BEST VALUE
          </div>

          <div className="text-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">Premium Yearly</h3>
            <div className="mt-4">
              <span className="font-heading font-black text-4xl text-lime-600">$79</span>
              <span className="text-slate-500">/year</span>
            </div>
            <p className="text-lime-600 font-bold text-sm mt-1">Save 34% vs monthly</p>
          </div>

          <ul className="space-y-3 mb-6">
            {premiumFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                <span className="text-slate-700">{feature.text}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => handleSubscribe("yearly")}
            disabled={isPremium || processingPlan === "yearly"}
            data-testid="subscribe-yearly-btn"
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processingPlan === "yearly" ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : isPremium ? (
              "Already Premium"
            ) : (
              "Subscribe Yearly"
            )}
          </button>
        </div>
      </div>

      {/* Features Breakdown */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-xl text-slate-800 mb-6 text-center">
          Why Go Premium?
        </h2>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-7 h-7 text-indigo-600" />
            </div>
            <h3 className="font-bold text-slate-800">Unlimited AI Tutor</h3>
            <p className="text-sm text-slate-500 mt-1">Ask Archie anything, anytime</p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 bg-lime-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <BarChart3 className="w-7 h-7 text-lime-600" />
            </div>
            <h3 className="font-bold text-slate-800">Advanced Analytics</h3>
            <p className="text-sm text-slate-500 mt-1">Deep insights into your progress</p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-bold text-slate-800">Exclusive Content</h3>
            <p className="text-sm text-slate-500 mt-1">Premium-only practice tests</p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Shield className="w-7 h-7 text-sky-600" />
            </div>
            <h3 className="font-bold text-slate-800">Ad-Free</h3>
            <p className="text-sm text-slate-500 mt-1">Focus on learning, not ads</p>
          </div>
        </div>
      </div>

      {/* Trust Badge */}
      <div className="text-center text-slate-400 text-sm">
        <p>🔒 Secure payment powered by Stripe</p>
        <p className="mt-1">Cancel anytime • No hidden fees</p>
      </div>
    </div>
  );
};

export default Membership;
